import { Api } from '../api';
import {
  ConversationListRequest,
  ConversationListResponse,
  ConversationMessagesRequest,
  ConversationMessagesResponse,
  ConversationStatusAssigned,
  ConversationStatusGroup,
  ConversationStatusResponse,
  MessageMediaRequest,
  UpdateConversationAssigneeRequest,
  UpdateConversationResponse,
  UpdateConversationStatusRequest,
  GetConversationTagsRequest,
  GetConversationTagsResponse,
  UpdateConversationTagsRequest,
  UpdateConversationTagsResponse,
  InternalNotesApiResponse,
  InternalNotesApiRequest,
  WaConfigData,
  ConversationThreadActionsRequest,
  ConversationThreadActionsResponse,
} from './model';
import { loadConversationStatusMap } from './slice';

export const conversationApi = Api.injectEndpoints({
  endpoints: (build) => ({
    getConversations: build.query<
      ConversationListResponse,
      ConversationListRequest
    >({
      query({ tags, teamMemberIds, channelTypes, channelSubTypes, ...rest }) {
        const searchParams = new URLSearchParams();

        tags?.forEach((id) => searchParams.append('tags', id));
        teamMemberIds?.forEach((id) => searchParams.append('assigneeId', id));
        channelTypes?.forEach((val) =>
          searchParams.append('channelTypes[]', val)
        );
        channelSubTypes?.forEach((val) =>
          searchParams.append('channelSubTypes[]', val)
        );
        Object.entries(rest).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
          }
        });
        searchParams.append('isReturnNewSemanticConversations', 'true');

        return {
          url: `hub/communication-v2/api/conversations/${
            rest.sellerWorkspaceId
          }?${searchParams.toString()}`,
          method: 'GET',
          cache: 'no-cache',
        };
      },
    }),
    updateStatus: build.mutation<
      UpdateConversationResponse,
      UpdateConversationStatusRequest
    >({
      query({ conversationId, sellerWorkspaceId, status }) {
        return {
          url: `hub/communication-v2/api/conversations/${conversationId}/status`,
          method: 'PUT',
          params: { sellerWorkspaceId },
          body: { status, isReturnNewSemanticConversations: true },
        };
      },
    }),
    updateAssignee: build.mutation<
      UpdateConversationResponse,
      UpdateConversationAssigneeRequest
    >({
      query(args) {
        return {
          url: `hub/communication-v2/api/conversations/${args.conversationId}/assign`,
          method: 'PUT',
          params: { sellerWorkspaceId: args.workspaceId },
          body: {
            isReturnNewSemanticConversations: true,
            teamMemberId: args.teamMemberId,
            type: args.type,
            workspaceId: args.workspaceId,
          },
        };
      },
    }),
    removeAssignee: build.mutation<
      UpdateConversationResponse,
      UpdateConversationAssigneeRequest
    >({
      query(args) {
        return {
          url: `hub/communication-v2/api/conversations/${args.conversationId}/unassign`,
          method: 'PUT',
          params: { sellerWorkspaceId: args.workspaceId },
          body: {
            isReturnNewSemanticConversations: true,
            teamMemberId: args.teamMemberId,
            workspaceId: args.workspaceId,
            type: args.type,
          },
        };
      },
    }),
    getConversationStatuses: build.query<
      ConversationStatusResponse,
      { sellerWorkspaceId: string }
    >({
      query({ sellerWorkspaceId }) {
        return {
          url: `hub/communication-v2/api/conversation-status/${sellerWorkspaceId}`,
          method: 'GET',
          params: { sellerWorkspaceId },
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then((response) => {
            const { data } = response;
            const statusMap: Record<
              ConversationStatusGroup | ConversationStatusAssigned,
              string[]
            > = {
              [ConversationStatusGroup.OPEN]: [],
              [ConversationStatusAssigned.ASSIGNED]: [],
              [ConversationStatusGroup.INPROGRESS]: [],
              [ConversationStatusGroup.CLOSED]: [],
            };
            data.statuses.forEach(({ category, status }) => {
              if (
                category === ConversationStatusGroup.INPROGRESS &&
                status === ConversationStatusAssigned.ASSIGNED
              ) {
                statusMap[ConversationStatusAssigned.ASSIGNED].push(status);
              } else {
                statusMap[category].push(status);
              }
            });
            dispatch(loadConversationStatusMap(statusMap));
          })
          .catch((error) =>
            console.log('getConversationStatuses error', error)
          );
      },
    }),
    getConversationMessage: build.query<
      ConversationMessagesResponse,
      ConversationMessagesRequest
    >({
      query: ({ conversationId, sellerWorkspaceId }) => ({
        url: `hub/communication-v2/api/conversations/detail/${conversationId}`,
        method: 'GET',
        params: {
          sellerWorkspaceId,
          isReturnNewSemanticConversations: true,
        },
      }),
    }),
    getMessageMediaUrl: build.mutation<string, MessageMediaRequest>({
      query(arg) {
        return {
          url: `sign/url`,
          method: 'POST',
          body: { destination: 'channels', ...arg },
        };
      },
      transformResponse: (response: { presignedUrl: string }) => {
        return response.presignedUrl;
      },
    }),
    getWhatsappConfig: build.query<
      WaConfigData[],
      { sellerWorkspaceId: string }
    >({
      query({ sellerWorkspaceId }) {
        return {
          url: `hub/orgs/api/whatsapp-config?sellerWorkspaceId=${sellerWorkspaceId}`,
          method: 'GET',
        };
      },
    }),
    getConversationTags: build.query<
      GetConversationTagsResponse,
      GetConversationTagsRequest
    >({
      query(args) {
        return {
          url: `hub/communication-v2/api/conversations/tags/${args.conversationId}?sellerWorkspaceId=${args.sellerWorkspaceId}&isReturnNewSemanticConversations=true`,
          method: 'GET',
        };
      },
    }),
    updateConversationTags: build.mutation<
      UpdateConversationTagsResponse,
      UpdateConversationTagsRequest
    >({
      query(args) {
        return {
          url: `hub/communication-v2/api/conversations/tags/${args.conversationId}?sellerWorkspaceId=${args.workspaceId}`,
          method: 'PUT',
          body: {
            tags: args.tags,
            workspaceId: args.workspaceId,
            isReturnNewSemanticConversations: true,
          },
        };
      },
    }),
    updateConversationTeamMemberRole: build.mutation<
      any,
      {
        teamMemberId: string;
        conversationId: string;
        type: 'assign' | 'watcher';
        workspaceId: string;
      }
    >({
      query(args) {
        return {
          url: `hub/communication-v2/api/conversations/${args.conversationId}/assign?sellerWorkspaceId=${args.workspaceId}`,
          method: 'PUT',
          body: {
            isReturnNewSemanticConversations: true,
            teamMemberId: args.teamMemberId,
            type: args.type,
            workspaceId: args.workspaceId,
          },
        };
      },
    }),
    getInternalNotes: build.query<
      InternalNotesApiResponse,
      InternalNotesApiRequest
    >({
      query({ customerId, limit, workspaceId, ...args }) {
        return {
          url: `hub/communication-v2/api/conversations/internal-notes/${customerId}`,
          method: 'GET',
          params: {
            ...args,
            ...(limit ? { limit } : {}),
            sellerWorkspaceId: workspaceId,
          },
        };
      },
    }),
    getConversationThreadActions: build.query<
      ConversationThreadActionsResponse,
      ConversationThreadActionsRequest
    >({
      query({ conversationThreadId, sellerWorkspaceId }) {
        return {
          url: `hub/communication-v2/api/conversation-threads/actions/${conversationThreadId}`,
          method: 'GET',
          params: { sellerWorkspaceId },
        };
      },
    }),
  }),
});

export const {
  useLazyGetConversationsQuery,
  useUpdateAssigneeMutation,
  useRemoveAssigneeMutation,
  useUpdateStatusMutation,
  useLazyGetConversationStatusesQuery,
  useLazyGetConversationMessageQuery,
  useGetMessageMediaUrlMutation,
  useLazyGetWhatsappConfigQuery,
  useLazyGetConversationTagsQuery,
  useUpdateConversationTagsMutation,
  useLazyGetInternalNotesQuery,
  useGetConversationThreadActionsQuery,
  useLazyGetConversationThreadActionsQuery,
} = conversationApi;
